<template>
  <div class="ats">
    <div v-if="deletingSellerId != null" class="ats-action-overlay" aria-live="polite">
      <div class="ats-action-overlay-inner">
        <div class="ats-spinner"></div>
        <p class="ats-action-overlay-text">Removing storefront…</p>
      </div>
    </div>
    <div v-if="loading" class="ats-loading"><div class="ats-spinner"></div></div>
    <div v-if="loadError && !loading" class="ats-error">
      <p class="ats-error-title">Could not load account settings</p>
      <p class="ats-error-msg">{{ loadError }}</p>
      <button class="ats-btn ats-btn-outline" @click="fetchAll">Retry</button>
    </div>

    <div v-if="!loading && !loadError">
      <!-- Header -->
      <div class="ats-header">
        <h2 class="ats-title">Account &amp; Team</h2>
        <p class="ats-subtitle">Manage your profile, connected Amazon accounts, team members, and integrations.</p>
      </div>

      <!-- ==================== ACCOUNT OVERVIEW ==================== -->
      <div class="ats-section-label">ACCOUNT OVERVIEW</div>
      <div class="ats-overview-grid">
        <div class="ats-ov-card">
          <span class="ats-ov-label">ACCOUNT OWNER</span>
          <span class="ats-ov-value">{{ firstName }} {{ lastName }}</span>
          <span class="ats-ov-sub"><a :href="'mailto:' + loginEmail" class="ats-link">{{ loginEmail }}</a></span>
        </div>
        <div class="ats-ov-card">
          <span class="ats-ov-label">PLAN</span>
          <span class="ats-ov-value">{{ planName }}</span>
          <span class="ats-ov-sub">{{ planDetail }}</span>
        </div>
        <div class="ats-ov-card">
          <span class="ats-ov-label">CONNECTED STOREFRONTS</span>
          <span class="ats-ov-value">{{ sellers.length }}</span>
          <span class="ats-ov-sub">{{ storefrontSummary }}</span>
        </div>
        <div class="ats-ov-card">
          <span class="ats-ov-label">TEAM MEMBERS</span>
          <span class="ats-ov-value">{{ teamUserAccounts.length }}</span>
          <span class="ats-ov-sub">{{ teamSummary }}</span>
        </div>
        <div class="ats-ov-card">
          <span class="ats-ov-label">ACCOUNT CREATED</span>
          <span class="ats-ov-value">{{ accountCreatedDisplay }}</span>
          <span class="ats-ov-sub">{{ billingCycleDisplay }}</span>
        </div>
      </div>

      <!-- ==================== PROFILE ==================== -->
      <div class="ats-section-label">PROFILE</div>
      <div class="ats-profile-row">
        <div class="ats-field">
          <label class="ats-field-label">FIRST NAME</label>
          <input type="text" v-model="firstName" class="ats-input" autocomplete="off" data-lpignore="true"/>
        </div>
        <div class="ats-field">
          <label class="ats-field-label">LAST NAME</label>
          <input type="text" v-model="lastName" class="ats-input" autocomplete="off" data-lpignore="true"/>
        </div>
      </div>
      <div class="ats-field" style="max-width:380px;">
        <label class="ats-field-label">LOGIN EMAIL</label>
        <div class="ats-input ats-input-readonly">{{ loginEmail }}</div>
      </div>
      <div class="ats-profile-actions">
        <button class="ats-btn ats-btn-outline" @click="changePassword">Change password</button>
        <button class="ats-btn ats-btn-primary" :disabled="!profileDirty" :class="{ 'ats-btn-loading': savingProfile }" @click="saveProfile">Save Changes</button>
      </div>

      <!-- ==================== CONNECTED STOREFRONTS ==================== -->
      <div class="ats-section-label">CONNECTED STOREFRONTS</div>
      <div class="ats-card">
        <div class="ats-card-header">
          <div>
            <h3 class="ats-card-title">Storefronts (Seller ID)</h3>
            <p class="ats-card-desc">Each connected storefront is monitored across all its marketplaces. Adding storefronts may affect your plan cost. Stop-all-alerts and stop-backend-attributes only halt outbound alerts or ATTR scans; dashboards and watchdogs keep updating.</p>
          </div>
          <button v-if="canAddStorefront" class="ats-btn ats-btn-outline" @click="addStorefront">+ Add Storefront</button>
        </div>
        <div class="ats-table-wrap">
          <table class="ats-table">
            <thead>
              <tr>
                <th>SELLER ID</th>
                <th>NICKNAME</th>
                <th>REGION</th>
                <th>MARKETPLACES</th>
                <th>STATUS</th>
                <th title="Overrides per-alert storefront and brand settings.">STOP ALL ALERTS</th>
                <th v-if="isOwner">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredSellers.length === 0">
                <td :colspan="isOwner ? 7 : 6" class="ats-empty">No storefronts connected yet.</td>
              </tr>
              <tr v-for="seller in filteredSellers" :key="sellerIdVal(seller)">
                <td class="ats-cell-mono">{{ sellerKeyVal(seller) }}</td>
                <td>
                  <input
                    v-if="isOwner"
                    type="text"
                    class="ats-input ats-input-sm"
                    :value="sellerNicknameDisplay(seller)"
                    @input="onNickNameInput(seller, $event.target.value)"
                    autocomplete="off"
                    data-lpignore="true"
                    spellcheck="false"
                  />
                  <span v-else class="ats-cell-text">{{ sellerNicknameDisplay(seller) || '—' }}</span>
                </td>
                <td class="ats-cell-center">{{ seller.region }}</td>
                <td>
                  <div class="ats-mp-badges">
                    <span
                      v-for="(mp, mpIdx) in sellerMarketplaceChips(seller)"
                      :key="sellerKeyVal(seller) + '-' + mp.flag + '-' + mpIdx"
                      class="ats-mp-badge"
                    >
                      <i :class="mp.flag + ' flag'"></i> {{ mp.label }}
                    </span>
                  </div>
                </td>
                <td>
                  <span v-if="seller.mayNeedReauthorization" class="ats-badge ats-badge-warn">Re-auth needed</span>
                  <span v-else class="ats-badge ats-badge-ok">Authorized</span>
                </td>
                <td class="ats-cell-center">
                  <label class="ats-toggle" :class="{ 'ats-toggle-busy': killSwitchBusy(seller) }">
                    <input
                      type="checkbox"
                      aria-label="Stop all alerts"
                      :checked="!sellerAlertsEnabled(seller)"
                      :disabled="!isOwner || killSwitchBusy(seller)"
                      @change="onStopToggle(seller, 'alertsEnabled', $event.target.checked)"
                    >
                    <span class="ats-toggle-slider"></span>
                  </label>
                </td>
                <td v-if="isOwner" class="ats-cell-actions">
                  <button class="ats-icon-btn" title="Re-authorize" @click="reauthorizeSeller(seller)">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 001.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"/></svg>
                  </button>
                  <button
                    type="button"
                    class="ats-icon-btn ats-icon-btn-danger"
                    title="Delete storefront"
                    :disabled="deletingSellerId != null"
                    @click="deleteSeller(seller)">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="ats-card-footer">
          <span v-if="isStarterPlan && !canAddStorefront" class="ats-note">Starter plan includes up to {{ starterMaxStorefronts }} storefronts.</span>
          <button
            v-if="isOwner"
            class="ats-btn ats-btn-primary"
            :disabled="!sellersDirty"
            :class="{ 'ats-btn-loading': savingSellers }"
            @click="saveSellers"
          >Save Changes</button>
          <p v-else class="ats-member-readonly-hint">View only — contact your account admin to add or edit storefronts.</p>
        </div>
      </div>

      <!-- ==================== TEAM ==================== -->
      <div class="ats-section-label">TEAM</div>
      <div class="ats-card">
        <div class="ats-card-header">
          <div>
            <h3 class="ats-card-title">Members &amp; Access</h3>
            <p class="ats-card-desc">Admin can configure alerts and manage all accounts. Members receive alerts for their assigned accounts only.</p>
          </div>
          <button v-if="isOwner" class="ats-btn ats-btn-outline" @click="inviteMember">+ Invite Member</button>
        </div>
        <div class="ats-table-wrap">
          <table class="ats-table">
            <thead>
              <tr>
                <th>MEMBER</th>
                <th>ROLE</th>
                <th>ASSIGNED ACCOUNTS</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="memberRows.length === 0">
                <td colspan="5" class="ats-empty">No team members yet.</td>
              </tr>
              <tr v-for="member in memberRows" :key="'m-' + (member.user.id != null ? member.user.id : member.user.email)">
                <td>
                  <div class="ats-member-info">
                    <span class="ats-member-name">{{ member.user.fullName || member.user.email }}</span>
                    <a :href="'mailto:' + member.user.email" class="ats-member-email">{{ member.user.email }}</a>
                  </div>
                </td>
                <td>
                  <span v-if="memberRoleIsLocked(member)" class="ats-badge ats-badge-role-admin">{{ memberRoleBadgeLabel(member) }}</span>
                  <select
                    v-else-if="isOwner && member.emailAddress"
                    class="ats-role-select"
                    :value="memberRoleSelectValue(member)"
                    :disabled="savingMemberRoleUserId != null"
                    @change="onMemberRoleChange(member, $event.target.value)"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <span v-else-if="member.user.isOwner" class="ats-badge ats-badge-role-admin">{{ memberRoleBadgeLabel(member) }}</span>
                  <span v-else class="ats-badge ats-badge-role-member">Member</span>
                </td>
                <td>
                  <div class="ats-account-pills">
                    <template v-if="member.user.isOwner">
                      <span class="ats-pill-account">All accounts</span>
                    </template>
                    <template v-else>
                      <span v-for="acc in memberAssignedAccounts(member)" :key="acc" class="ats-pill-account">{{ acc }}</span>
                      <a v-if="isOwner && !member.user.isOwner && member.emailAddress" href="#" class="ats-edit-link" @click.prevent="openAssignSellersModal(member)">+ Edit</a>
                    </template>
                  </div>
                </td>
                <td>
                  <div class="ats-status-cell">
                    <span v-if="memberIsVerified(member)" class="ats-badge ats-badge-ok">Verified</span>
                    <span v-else class="ats-badge ats-badge-warn">Not verified</span>
                    <button
                      v-if="showVerifyEmailButton(member)"
                      type="button"
                      class="ats-btn ats-btn-outline ats-btn-verify-email"
                      :disabled="verificationEmailSendingId === memberVerificationButtonKey(member)"
                      :class="{ 'ats-btn-loading': verificationEmailSendingId === memberVerificationButtonKey(member) }"
                      @click="sendMemberVerificationEmail(member)"
                    >
                      Resend Verification Email
                    </button>
                  </div>
                </td>
                <td class="ats-cell-actions">
                  <span v-if="member.user.isOwner" class="ats-text-muted">&mdash;</span>
                  <button v-else-if="isOwner" class="ats-icon-btn ats-icon-btn-danger" title="Remove member" @click="removeMember(member)">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="ats-card-footer">
          <span class="ats-note">Account assignment changes are saved when you click Done in the assignment dialog.</span>
        </div>
      </div>

      <!-- ==================== INTEGRATIONS ==================== -->
      <div class="ats-section-label">INTEGRATIONS</div>
      <div class="ats-integrations-grid">
        <!-- Google Sheets -->
        <div class="ats-int-card">
          <div class="ats-int-icon">
            <img :src="googleSheetsLogo" alt="" class="ats-int-logo" />
          </div>
          <div class="ats-int-body">
            <span class="ats-int-name">Google Sheets</span>
            <span v-if="gsheetsConnection.email" class="ats-int-detail">{{ gsheetsConnection.email }}</span>
            <span v-else class="ats-int-detail">Export alerts to a spreadsheet</span>
          </div>
          <div class="ats-int-actions">
            <template v-if="gsheetsConnection.email && gsheetsConnection.email !== 'pending'">
              <span class="ats-badge ats-badge-ok">Connected</span>
              <a v-if="gsheetsConnection.spreadsheetId" href="#" class="ats-link" @click.prevent="openGoogleSheet">Open &rarr;</a>
              <a href="#" class="ats-link ats-link-danger" @click.prevent="revokeGoogleSheets">Revoke</a>
            </template>
            <button v-else class="ats-btn ats-btn-connect" @click="connectGoogleSheets">Connect</button>
          </div>
        </div>
        <!-- Zapier -->
        <div class="ats-int-card">
          <div class="ats-int-icon">
            <img :src="zapierLogo" alt="" class="ats-int-logo" />
          </div>
          <div class="ats-int-body">
            <span class="ats-int-name">Zapier</span>
            <span v-if="hasZapier" class="ats-int-detail">Connected{{ zapierConnectedSince ? ' · ' + zapierConnectedSince : '' }}</span>
            <span v-else class="ats-int-detail">Trigger Zaps from SentryKit alerts</span>
          </div>
          <div class="ats-int-actions">
            <template v-if="hasZapier">
              <span class="ats-badge ats-badge-ok">Connected</span>
              <a :href="zapierTriggersDocUrl" target="_blank" rel="noopener noreferrer" class="ats-link">Triggers &rarr;</a>
              <a href="#" class="ats-link ats-link-danger" @click.prevent="revokeZapier">Revoke</a>
            </template>
            <a v-else :href="zapierDirectoryUrl" target="_blank" rel="noopener noreferrer" class="ats-btn ats-btn-connect">Connect</a>
          </div>
        </div>
      </div>

      <!-- Success / Error toast -->
      <transition name="ats-slide-up">
        <div v-if="successMsg" class="ats-toast ats-toast-ok">{{ successMsg }}</div>
      </transition>
      <transition name="ats-slide-up">
        <div v-if="errorMsg" class="ats-toast ats-toast-err">{{ errorMsg }}</div>
      </transition>
    </div>

    <!-- Modals -->
    <change-password-modal ref="changePasswordModal" />
    <div id="addSellerAccountModal" class="ui modal add-storefront-modal">
      <i class="close icon"></i>
      <div class="header">Connect Amazon storefront</div>
      <div class="content">
        <p class="add-storefront-modal-desc">Approve SentryKit on Amazon.</p>
        <add-seller-account @error="onAddSellerError" :show-nickname="true" />
      </div>
    </div>

    <div id="assignSellersModal" class="ui small modal">
      <i class="close icon"></i>
      <div class="header">Assign storefronts</div>
      <div v-if="assignModalMember" class="content">
        <p class="assign-modal-intro">
          Choose which connected storefronts <strong>{{ assignModalMember.user.fullName || assignModalMember.user.email }}</strong> can access. They only receive alerts for assigned accounts.
        </p>
        <div v-if="!sellers.length" class="assign-modal-empty">No storefronts connected yet.</div>
        <div v-else class="assign-seller-list">
          <label
            v-for="seller in sellers"
            :key="'assign-seller-' + sellerIdVal(seller)"
            class="assign-seller-row"
          >
            <input
              type="checkbox"
              :checked="assignModalSellerChecked(seller)"
              @change="assignModalToggleSeller(seller)"
            />
            <span class="assign-seller-label">{{ seller.nickName || sellerKeyVal(seller) }}</span>
            <span class="assign-seller-meta">{{ sellerKeyVal(seller) }}</span>
          </label>
        </div>
      </div>
      <div class="actions">
        <button type="button" class="ui button" :disabled="savingAssignModal" @click="confirmAssignSellersModal">
          {{ savingAssignModal ? 'Saving…' : 'Done' }}
        </button>
      </div>
    </div>

    <div id="inviteMemberModal" class="ui small modal">
      <i class="close icon"></i>
      <div class="header">Invite team member</div>
      <div class="content">
        <p class="invite-modal-intro">
          We will email them an invitation. Only the account owner can add users.
        </p>
        <div class="invite-modal-form">
          <div class="ats-field">
            <label class="ats-field-label">EMAIL</label>
            <input
              v-model="inviteEmail"
              type="email"
              class="ats-input"
              autocomplete="off"
              data-lpignore="true"
              placeholder="colleague@company.com"
              @keyup.enter="submitInviteMember"
            />
          </div>
          <div class="invite-modal-name-row">
            <div class="ats-field">
              <label class="ats-field-label">FIRST NAME</label>
              <input v-model="inviteFirstName" type="text" class="ats-input" autocomplete="off" data-lpignore="true" />
            </div>
            <div class="ats-field">
              <label class="ats-field-label">LAST NAME</label>
              <input v-model="inviteLastName" type="text" class="ats-input" autocomplete="off" data-lpignore="true" />
            </div>
          </div>
        </div>
      </div>
      <div class="actions">
        <button type="button" class="ui button" :disabled="inviteSubmitting" @click="closeInviteMemberModal">Cancel</button>
        <button
          type="button"
          class="ui primary button"
          :class="{ loading: inviteSubmitting, disabled: inviteSubmitting }"
          :disabled="inviteSubmitting || !inviteEmailTrimmed"
          @click="submitInviteMember"
        >
          Send invitation
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import ChangePasswordModal from './ChangePasswordModal'
import AddSellerAccount from './AddSellerAccount.vue'
import { amazonAuthorizationQueryMarketplace } from '../../utils/amazonSellingPartnerOAuth'
import { extractV1SellersItems } from '../../utils/sellerAccountsFromV1Api.js'
import { flagsFromSellerRow, mergeKillSwitchFlags, patchBodyForStopToggle } from '../../utils/sellerKillSwitches.js'
import { formatAccountPlanPricingSubline } from '../../utils/productAddonBilling.js'
import {
  canAddStorefrontForPlan,
  isStarterOrderVolumePlan,
  STARTER_MAX_STOREFRONTS
} from '../../utils/orderVolumeBilling.js'

var REGION_MARKETPLACE = { NA: 'us', EU: 'gb', IN: 'in', ME: 'ae', FE: 'jp' }
var MP_LABELS = { us: 'US', gb: 'UK', uk: 'UK', ca: 'CA', de: 'DE', fr: 'FR', it: 'IT', es: 'ES', jp: 'JP', au: 'AU', 'in': 'IN', mx: 'MX', br: 'BR', nl: 'NL', sa: 'SA', ae: 'AE', sg: 'SG', se: 'SE', pl: 'PL', be: 'BE', at: 'AT', za: 'ZA', tr: 'TR', eg: 'EG' }
/** Team seller scope: bulk upsert seller ids for a user. */
var NOTIFICATION_PREFS_SELLERS_URL_V1 = '/api/v1/notification-preferences/sellers'
var googleSheetsLogo = require('../../assets/integrations/google-sheets.png')
var zapierLogo = require('../../assets/integrations/zapier.png')

function userAccountRoleNorm (ua) {
  var r = String((ua && ua.role) != null ? ua.role : 'member').toLowerCase()
  if (r === 'admin' || r === 'owner') return 'admin'
  return 'member'
}

function userAccountIsWorkspaceOwner (ua) {
  return ua && (ua.is_owner === true || ua.isOwner === true)
}

function identitySettingsIsOwner (body) {
  if (!body || typeof body !== 'object') return false
  if (body.isOwner === true || body.is_owner === true) return true
  if (body.isOwner === false || body.is_owner === false) return false
  /** Local APIs sometimes omit the flag; treat as owner so Add Storefront stays available. */
  if (process.env.NODE_ENV === 'development') return true
  return false
}

function identitySettingsSellerAccounts (body) {
  if (!body || typeof body !== 'object') return []
  if (Array.isArray(body.sellerAccounts)) return body.sellerAccounts
  if (Array.isArray(body.seller_accounts)) return body.seller_accounts
  if (Array.isArray(body.sellers)) return body.sellers
  return []
}

function destroySemanticModal (selector) {
  try {
    var $m = $(selector)
    if (!$m.length || !$m.data('module-modal')) return
    $m.modal('hide')
    $m.modal('destroy')
  } catch (e) {}
}

function clearStuckPageDimmer () {
  try {
    $('body').removeClass('dimmed')
    var $dimmer = $('.ui.dimmer.modals')
    $dimmer.removeClass('visible active animating')
    $dimmer.find('.ui.modal').removeClass('visible active animating')
    $dimmer.css({ display: 'none', 'background-color': '' })
  } catch (e) {}
}

function userAccountIsAdminRow (ua) {
  if (!ua) return false
  if (userAccountIsWorkspaceOwner(ua)) return true
  return userAccountRoleNorm(ua) === 'admin'
}

/** From GET /api/identity/settings `user_account[]`: `emailVerified` / `email_verified`. If absent, assume verified (legacy API). */
function userAccountEmailVerifiedFromSettings (ua) {
  if (!ua) return true
  var v = ua.emailVerified
  if (v === undefined || v === null) v = ua.email_verified
  if (v === undefined || v === null) return true
  return v === true || v === 'true' || v === 1 || v === '1'
}

export default {
  name: 'AccountTeamSettings',
  components: { ChangePasswordModal, AddSellerAccount },
  data: function () {
    return {
      googleSheetsLogo: googleSheetsLogo,
      zapierLogo: zapierLogo,
      loading: false,
      loadError: null,
      isOwner: false,
      loginEmail: '',
      firstName: '',
      firstNameOrig: '',
      lastName: '',
      lastNameOrig: '',
      sellers: [],
      filteredSellers: [],
      sellerNickNames: {},
      savingKillSwitches: {},
      teamUserAccounts: [],
      tenantUsers: [],
      packageDetails: null,
      addonCount: 0,
      /** From GET /api/identity/settings `dailyPricing` — daily ASIN meter billing. */
      hasDailyPricing: false,
      gsheetsConnection: { enabled: false, email: null, spreadsheetId: null, connectionError: false },
      authorizations: [],
      savingProfile: false,
      savingSellers: false,
      savingTeam: false,
      successMsg: '',
      errorMsg: '',
      assignModalMember: null,
      savingAssignModal: false,
      deletingSellerId: null,
      savingMemberRoleUserId: null,
      inviteEmail: '',
      inviteFirstName: '',
      inviteLastName: '',
      inviteSubmitting: false,
      verificationEmailSendingId: null
    }
  },
  computed: {
    profileDirty: function () {
      return this.firstName !== this.firstNameOrig || this.lastName !== this.lastNameOrig
    },
    sellersDirty: function () {
      var self = this
      return this.filteredSellers.some(function (s) {
        var id = self.sellerIdVal(s)
        return self.sellerNickNames[id] !== undefined && self.sellerNickNames[id] !== s.nickName
      })
    },
    teamDirty: function () {
      if (!this.isOwner) return false
      var self = this
      return this.memberRows.some(function (row) {
        var ea = row.emailAddress
        if (!ea || row.user.isOwner || ea.isDeleted) return false
        return !self.sellerAccessEqual(ea)
      })
    },
    planName: function () {
      if (this.hasDailyPricing) return 'SentryKit Daily Usage Plan'
      if (!this.packageDetails) return '—'
      return this.packageDetails.name || this.packageDetails.packageName || 'Starter'
    },
    planDetail: function () {
      if (this.hasDailyPricing) return 'Billed monthly based on the number of ASINs you track'
      return formatAccountPlanPricingSubline(this.packageDetails, this.addonCount)
    },
    starterMaxStorefronts: function () {
      return STARTER_MAX_STOREFRONTS
    },
    isStarterPlan: function () {
      if (this.hasDailyPricing) return false
      return isStarterOrderVolumePlan(this.packageDetails, null, {
        subscriptionSummary: this.$root && this.$root.subscriptionSummary
      })
    },
    canAddStorefront: function () {
      if (!this.isOwner) return false
      /** Local/dev: always allow adding storefronts so OAuth connect can be tested. */
      if (process.env.NODE_ENV === 'development') return true
      return canAddStorefrontForPlan(this.packageDetails, this.sellers.length, {
        subscriptionSummary: this.$root && this.$root.subscriptionSummary
      })
    },
    storefrontSummary: function () {
      if (!this.sellers.length) return 'None connected'
      return this.sellers.map(function (s) { return s.nickName || s.region }).join(' · ')
    },
    teamSummary: function () {
      if (this.teamUserAccounts.length) {
        var admins = 0
        var members = 0
        this.teamUserAccounts.forEach(function (a) {
          if (userAccountIsAdminRow(a)) admins++
          else members++
        })
        var parts = []
        if (admins) parts.push(admins + ' admin' + (admins > 1 ? 's' : ''))
        if (members) parts.push(members + ' member' + (members > 1 ? 's' : ''))
        return parts.join(' · ') || '—'
      }
      var owners = this.tenantUsers.filter(function (u) { return u.isOwner }).length
      var mem = this.tenantUsers.length - owners
      var p = []
      if (owners) p.push(owners + ' admin')
      if (mem) p.push(mem + ' member' + (mem > 1 ? 's' : ''))
      return p.join(' · ') || '—'
    },
    inviteEmailTrimmed: function () {
      return (this.inviteEmail || '').trim()
    },
    accountCreatedDisplay: function () {
      if (this.packageDetails && this.packageDetails.createdAt) {
        return new Date(this.packageDetails.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      }
      return '—'
    },
    billingCycleDisplay: function () {
      return this.packageDetails ? 'Monthly billing' : ''
    },
    zapierAuthorizations: function () {
      return this.authorizations.filter(function (a) { return a.name === 'Zapier' })
    },
    hasZapier: function () {
      return this.zapierAuthorizations.length > 0
    },
    zapierConnectedSince: function () {
      var list = this.zapierAuthorizations
      if (!list.length || !list[0].timestamp) return ''
      return 'since ' + new Date(list[0].timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    zapierDirectoryUrl: function () {
      return process.env.ZAPIER_DIRECTORY_URL || 'https://zapier.com/apps/sentrykit/integrations'
    },
    zapierTriggersDocUrl: function () {
      return process.env.ZAPIER_TRIGGERS_DOC_URL || 'https://docs.google.com/document/d/15O8b4mBsAb7ccIrL05jcxJCuo9_kDemcXkgjwMy_VA4/edit?usp=sharing'
    },
    memberRows: function () {
      var self = this
      if (this.teamUserAccounts.length) {
        return this.teamUserAccounts.map(function (account) {
          var accountEmail = account.email || ''
          var matchedUser = null
          for (var i = 0; i < self.tenantUsers.length; i++) {
            var tenantUser = self.tenantUsers[i]
            if (tenantUser.email && String(tenantUser.email).toLowerCase() === String(accountEmail).toLowerCase()) {
              matchedUser = tenantUser
              break
            }
          }
          var isAdminRow = userAccountIsAdminRow(account)
          var userId = account.userId != null ? account.userId : (matchedUser ? matchedUser.id : null)
          var user = {
            id: userId,
            email: accountEmail || (matchedUser ? matchedUser.email : ''),
            firstName: (account.firstName || (matchedUser ? matchedUser.firstName : '') || ''),
            lastName: (account.lastName || (matchedUser ? matchedUser.lastName : '') || ''),
            fullName: (account.fullName || (matchedUser ? matchedUser.fullName : '') || ''),
            isOwner: !!isAdminRow
          }
          if (!Array.isArray(account.allowedSellerAccounts)) account.allowedSellerAccounts = []
          if (!Array.isArray(account.originalallowedSellerAccounts)) {
            account.originalallowedSellerAccounts = account.allowedSellerAccounts.map(function (x) { return x })
          }
          account.isDeleted = false
          account.isPrimary = !!isAdminRow
          account.isUser = !isAdminRow
          account.email = user.email
          return { user: user, emailAddress: account }
        })
      }
      return []
    },
  },
  watch: {
    profileDirty: 'syncGlobalDirty',
    sellersDirty: 'syncGlobalDirty',
    teamDirty: 'syncGlobalDirty'
  },
  created: function () { window.dirty = false },
  mounted: function () {
    clearStuckPageDimmer()
    this.fetchAll()
    this.initAddStorefrontModal()
  },
  beforeDestroy: function () {
    destroySemanticModal('#addSellerAccountModal')
    clearStuckPageDimmer()
  },
  beforeRouteLeave: function (to, from, next) {
    if (window.dirty) {
      if (window.confirm('You have unsaved changes. Leave anyway?')) { window.dirty = false; next() } else { next(false) }
    } else { next() }
  },
  methods: {
    fetchAll: function () {
      this.loading = true
      this.loadError = null
      var self = this
      Promise.all([
        this.$http.get('/api/identity/settings'),
        this.$http.get('/api/identity/users'),
        this.$http.get('/api/billing/payment-status', { params: { timezoneOffsetMinutes: 0 - new Date().getTimezoneOffset() } }).catch(function () { return null }),
        this.$http.get('/api/googlesheets/connection-status').catch(function () { return null }),
        this.$http.get('/api/identity/authorizations').catch(function () { return null })
      ]).then(function (results) {
        self.loading = false
        self.processSettings(results[0])
        self.ensureTenantStorefrontsLoaded()
        self.processUsers(results[1])
        if (results[2]) self.processBilling(results[2])
        if (results[3]) self.gsheetsConnection = results[3].body || self.gsheetsConnection
        if (results[4]) self.authorizations = (results[4].body || [])
      }).catch(function (err) {
        self.loading = false
        var body = (err && err.body) || {}
        self.loadError = body.message || 'Failed to load settings'
      })
    },
    processSettings: function (response) {
      var body = response.body || {}
      this.hasDailyPricing = !!body.dailyPricing
      this.loginEmail = body.email || ''
      this.isOwner = identitySettingsIsOwner(body)
      this.firstName = body.firstName || body.first_name || ''
      this.firstNameOrig = this.firstName
      this.lastName = body.lastName || body.last_name || ''
      this.lastNameOrig = this.lastName
      var rawSellers = identitySettingsSellerAccounts(body)
      this.sellers = rawSellers
      var rawUserAccounts = Array.isArray(body.user_account) ? body.user_account : []
      this.teamUserAccounts = rawUserAccounts.map(function (ua) {
        var sellerIds = Array.isArray(ua.sellerIds) ? ua.sellerIds : (Array.isArray(ua.seller_ids) ? ua.seller_ids : [])
        var emailAddrId = ua.emailAddressId != null ? ua.emailAddressId : (ua.email_address_id != null ? ua.email_address_id : null)
        if (emailAddrId == null && ua.userAccountId != null) emailAddrId = ua.userAccountId
        if (emailAddrId == null && ua.user_account_id != null) emailAddrId = ua.user_account_id
        if (emailAddrId == null && ua.id != null) emailAddrId = ua.id
        var wsOwner = userAccountIsWorkspaceOwner(ua)
        var roleNorm = userAccountRoleNorm(ua)
        var adminRow = userAccountIsAdminRow(ua)
        var fn = ua.firstName != null ? ua.firstName : ua.first_name
        var ln = ua.lastName != null ? ua.lastName : ua.last_name
        var full = ua.fullName != null ? ua.fullName : ua.full_name
        if (!full && (fn || ln)) full = [fn, ln].filter(Boolean).join(' ')
        return {
          userId: ua.userId != null ? ua.userId : ua.user_id,
          email: ua.email || '',
          role: ua.role != null ? ua.role : 'member',
          roleNormalized: roleNorm,
          is_owner: wsOwner,
          isOwner: wsOwner,
          firstName: fn || '',
          lastName: ln || '',
          fullName: full || '',
          emailAddressId: emailAddrId,
          allowedSellerAccounts: sellerIds.map(function (id) { return id }),
          originalallowedSellerAccounts: sellerIds.map(function (id) { return id }),
          isDeleted: false,
          isPrimary: adminRow,
          isUser: !adminRow,
          emailVerified: userAccountEmailVerifiedFromSettings(ua)
        }
      })
      this.syncRootEmailVerificationFromTeam()
      this.applyStorefrontDisplayList(rawSellers)
    },
    /** Keep the global verify-account banner in sync with Account settings. */
    syncRootEmailVerificationFromTeam: function () {
      var root = this.$root
      if (!root || !root.applyEmailVerificationStatus) return
      var login = String(this.loginEmail || root.userEmail || '').toLowerCase()
      if (!login) return
      var match = this.teamUserAccounts.filter(function (ua) {
        return String(ua.email || '').toLowerCase() === login
      })[0]
      if (!match) return
      // Don't overwrite a verified=true from email-addresses with stale settings false.
      if (root.emailVerified === true && !match.emailVerified) return
      root.applyEmailVerificationStatus({
        emailVerified: !!match.emailVerified,
        email: match.email,
        emailAddressId: match.emailAddressId
      })
    },
    /** All tenant storefronts in the table (members see the same list as admin, read-only). */
    applyStorefrontDisplayList: function (list) {
      this.filteredSellers = Array.isArray(list) ? list.slice() : []
      this.sellerNickNames = {}
    },
    mapV1SellerRowToStorefront: function (row) {
      if (!row || typeof row !== 'object') return null
      var sid = row.sellerId != null ? row.sellerId : row.seller_id
      if (sid != null && typeof sid === 'object' && sid.value !== undefined) sid = sid.value
      if (sid == null || sid === '') sid = row.id
      if (sid != null && typeof sid === 'object' && sid.value !== undefined) sid = sid.value
      if (sid == null || sid === '') return null
      var skey = row.sellerKey != null ? row.sellerKey : row.seller_key
      if (skey != null && typeof skey === 'object' && skey.value !== undefined) skey = skey.value
      var mp = row.marketplace
      if (mp == null || mp === '') mp = row.marketplaces || row.marketplaceCodes || row.marketplace_codes
      var flags = flagsFromSellerRow(row)
      return {
        sellerId: sid,
        sellerKey: skey != null ? skey : sid,
        nickName: row.nickName || row.nickname || row.accountNickName || row.name || '',
        region: row.region || row.marketplaceRegion || 'NA',
        marketplace: mp,
        marketplaceId: row.marketplaceId || row.marketplace_id || null,
        mayNeedReauthorization: !!(row.mayNeedReauthorization || row.may_need_reauthorization),
        setupCompleted: row.setupCompleted !== false && row.setup_completed !== false,
        alertsEnabled: flags.alertsEnabled
      }
    },
    ensureTenantStorefrontsLoaded: function () {
      var self = this
      var identitySellers = Array.isArray(this.sellers) ? this.sellers.slice() : []
      return this.$http.get('/api/v1/sellers').then(function (res) {
        var items = extractV1SellersItems(res.body || {})
        var mapped = items.map(function (row) { return self.mapV1SellerRowToStorefront(row) }).filter(Boolean)
        if (mapped.length) {
          self.sellers = mapped
          self.applyStorefrontDisplayList(mapped)
        } else {
          var mappedIdentity = identitySellers.map(function (row) {
            return self.mapV1SellerRowToStorefront(row) || row
          }).filter(Boolean)
          self.applyStorefrontDisplayList(mappedIdentity.length ? mappedIdentity : identitySellers)
        }
      }, function () {
        var mappedIdentityFail = identitySellers.map(function (row) {
          return self.mapV1SellerRowToStorefront(row) || row
        }).filter(Boolean)
        self.applyStorefrontDisplayList(mappedIdentityFail.length ? mappedIdentityFail : identitySellers)
      })
    },
    processUsers: function (response) {
      var body = response.body || {}
      var list = Array.isArray(body.users) ? body.users : []
      this.tenantUsers = list.map(function (u) {
        var fn = u.first_name != null ? u.first_name : u.firstName
        var ln = u.last_name != null ? u.last_name : u.lastName
        var full = u.full_name != null ? u.full_name : u.fullName
        var em = u.email || u.user_email || u.userEmail
        if (!full && (fn || ln)) full = [fn, ln].filter(Boolean).join(' ')
        return {
          id: u.id != null ? u.id : (u.user_id != null ? u.user_id : (u.userId != null ? u.userId : (u.uid != null ? u.uid : (u.user && (u.user.id || u.user.user_id))))),
          email: em,
          firstName: fn,
          lastName: ln,
          fullName: full || '',
          isOwner: !!(u.is_owner != null ? u.is_owner : u.isOwner)
        }
      })
    },
    processBilling: function (response) {
      var body = response.body || {}
      this.packageDetails = body.packageDetails || null
      var pd = this.packageDetails
      this.addonCount =
        body.addonCount != null
          ? body.addonCount
          : (pd && pd.addonCount != null ? pd.addonCount : 0)
    },
    sellerIdVal: function (s) {
      if (!s) return null
      if (s.sellerId != null) {
        if (typeof s.sellerId === 'object' && s.sellerId.value !== undefined) return s.sellerId.value
        return s.sellerId
      }
      if (s.seller_id != null) {
        if (typeof s.seller_id === 'object' && s.seller_id.value !== undefined) return s.seller_id.value
        return s.seller_id
      }
      if (s.id != null) return s.id
      return null
    },
    sellerKeyVal: function (s) {
      if (!s) return ''
      if (s.sellerKey != null) {
        if (typeof s.sellerKey === 'object' && s.sellerKey.value !== undefined) return s.sellerKey.value
        if (typeof s.sellerKey !== 'object') return s.sellerKey
      }
      if (s.seller_key != null && typeof s.seller_key !== 'object') return s.seller_key
      var id = this.sellerIdVal(s)
      return id != null ? id : ''
    },
    sellerMarketplaceFlag: function (s) { var mp = s.marketplaceIso31662Code || REGION_MARKETPLACE[s.region] || 'us'; return mp.toLowerCase() },
    sellerMarketplaceCode: function (s) { var mp = this.sellerMarketplaceFlag(s); return MP_LABELS[mp] || mp.toUpperCase() },
    sellerMarketplaceChips: function (s) {
      var self = this
      var arr = s && (s.marketplace || s.marketplaces || s.marketplaceCodes || s.marketplace_codes)
      if (arr && !Array.isArray(arr) && typeof arr === 'string') arr = arr.split(',')
      if (Array.isArray(arr) && arr.length > 0) {
        var seen = Object.create(null)
        var chips = []
        for (var i = 0; i < arr.length; i++) {
          var raw = arr[i]
          if (raw == null || raw === '') continue
          if (typeof raw === 'object') {
            raw = raw.code || raw.marketplace || raw.marketplaceCode || raw.marketplace_code || raw.countryCode
          }
          if (raw == null || raw === '') continue
          var code = String(raw).trim().toLowerCase()
          if (code === 'gb') code = 'uk'
          if (seen[code]) continue
          seen[code] = true
          chips.push({ flag: code === 'uk' ? 'gb' : code, label: MP_LABELS[code] || code.toUpperCase() })
        }
        if (chips.length > 0) return chips
      }
      var f = self.sellerMarketplaceFlag(s)
      return [{ flag: f, label: self.sellerMarketplaceCode(s) }]
    },
    sellerNicknameDisplay: function (seller) {
      var id = this.sellerIdVal(seller)
      if (id != null && id !== '' && this.sellerNickNames[id] !== undefined) {
        return this.sellerNickNames[id]
      }
      return seller && seller.nickName != null ? seller.nickName : ''
    },
    onNickNameInput: function (seller, value) {
      var id = this.sellerIdVal(seller)
      if (id == null || id === '') return
      this.$set(this.sellerNickNames, id, value)
    },
    sellerAlertsEnabled: function (seller) {
      return flagsFromSellerRow(seller).alertsEnabled
    },
    killSwitchKey: function (seller, field) {
      var id = this.sellerIdVal(seller)
      if (id == null || id === '') return ''
      return String(id) + ':' + field
    },
    killSwitchBusy: function (seller) {
      var id = this.sellerIdVal(seller)
      if (id == null || id === '') return false
      var sid = String(id)
      return !!this.savingKillSwitches[sid + ':alertsEnabled']
    },
    patchSellerOnLists: function (id, updated) {
      var self = this
      function apply (list) {
        if (!Array.isArray(list)) return
        for (var i = 0; i < list.length; i++) {
          if (String(self.sellerIdVal(list[i])) === String(id)) {
            self.$set(list, i, Object.assign({}, list[i], updated))
            break
          }
        }
      }
      apply(this.filteredSellers)
      apply(this.sellers)
    },
    onStopToggle: function (seller, field, stopChecked) {
      if (!this.isOwner) return
      var id = this.sellerIdVal(seller)
      if (id == null || id === '') return
      if (this.killSwitchBusy(seller)) return
      var key = this.killSwitchKey(seller, field)
      if (!key) return
      var prev = Object.assign({}, seller)
      var body = patchBodyForStopToggle(field, stopChecked)
      var optimistic = mergeKillSwitchFlags(seller, body)
      this.patchSellerOnLists(id, optimistic)
      this.$set(this.savingKillSwitches, key, true)
      var self = this
      this.$http.patch('/api/v1/sellers/' + encodeURIComponent(String(id)), body).then(function (res) {
        self.$set(self.savingKillSwitches, key, false)
        self.patchSellerOnLists(id, mergeKillSwitchFlags(optimistic, res.body || {}))
      }, function (err) {
        self.$set(self.savingKillSwitches, key, false)
        self.patchSellerOnLists(id, prev)
        if (err && err.status === 401) return
        var msg = err && err.body && err.body.message
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ')
        self.flashError((typeof msg === 'string' && msg) || (err && err.message) || 'Could not update seller alerts')
      })
    },
    memberAssignedAccounts: function (member) {
      if (!member.emailAddress || !member.emailAddress.allowedSellerAccounts) return []
      var self = this
      return member.emailAddress.allowedSellerAccounts.map(function (sid) {
        var seller = self.sellers.filter(function (s) { return String(self.sellerIdVal(s)) === String(sid) })[0]
        return seller ? seller.nickName : String(sid)
      })
    },
    memberIsVerified: function (member) {
      return member.emailAddress ? !!member.emailAddress.emailVerified : false
    },
    /** Scalar id for POST /api/identity/emailverification/:id — falls back to user.id */
    memberEmailAddressId: function (member) {
      var ea = member && member.emailAddress
      if (ea) {
        var id = ea.emailAddressId != null ? ea.emailAddressId : ea.email_address_id
        if (id != null && typeof id === 'object' && id.value !== undefined) id = id.value
        if (id != null && id !== '') return id
      }
      var uid = member && member.user && member.user.id != null ? member.user.id : null
      return uid != null && uid !== '' ? uid : null
    },
    memberVerificationButtonKey: function (member) {
      var id = this.memberEmailAddressId(member)
      return id != null ? String(id) : ''
    },
    isSelfMemberRow: function (member) {
      if (!member || !member.user || !member.user.email || !this.loginEmail) return false
      return String(member.user.email).toLowerCase() === String(this.loginEmail).toLowerCase()
    },
    canSendVerificationForMember: function (member) {
      return this.isOwner || this.isSelfMemberRow(member)
    },
    showVerifyEmailButton: function (member) {
      if (this.memberIsVerified(member)) return false
      if (!this.memberEmailAddressId(member)) return false
      return this.canSendVerificationForMember(member)
    },
    sendMemberVerificationEmail: function (member) {
      var self = this
      if (!this.showVerifyEmailButton(member)) return
      var addrId = this.memberEmailAddressId(member)
      if (addrId == null || addrId === '') {
        this.flashError('Missing email record for this member.')
        return
      }
      var idKey = String(addrId)
      this.verificationEmailSendingId = idKey
      this.errorMsg = ''
      this.$http.post('/api/identity/emailverification/' + encodeURIComponent(idKey)).then(function () {
        self.verificationEmailSendingId = null
        var email = (member.user && member.user.email) ? member.user.email : ''
        self.flash('Verification email sent to ' + email)
      }).catch(function (err) {
        self.verificationEmailSendingId = null
        var b = err && err.body
        var msg = (b && (b.message || b.error)) || err.message || 'Could not send verification email'
        self.flashError(typeof msg === 'string' ? msg : 'Could not send verification email')
      })
    },
    sellerAccessEqual: function (ea) {
      var o = new Set((ea.originalallowedSellerAccounts || []).map(String))
      var c = new Set((ea.allowedSellerAccounts || []).map(String))
      if (o.size !== c.size) return false
      var ok = true
      o.forEach(function (x) { if (!c.has(x)) ok = false })
      return ok
    },
    syncGlobalDirty: function () {
      window.dirty = !!(this.profileDirty || this.sellersDirty || this.teamDirty)
    },
    memberRoleIsLocked: function (member) {
      var ea = member && member.emailAddress
      if (!ea) return true
      return ea.is_owner === true || ea.isOwner === true
    },
    /** Role pill text: workspace owner + admin role → "Admin & Owner". */
    memberRoleBadgeLabel: function (member) {
      var ea = member && member.emailAddress
      var u = member && member.user
      var ws = ea && (ea.is_owner === true || ea.isOwner === true)
      var r = ea ? String(ea.role || '').toLowerCase() : ''
      var adminRole = r === 'admin' || r === 'owner'
      if (ws && adminRole) return 'Admin & Owner'
      if (ws) return 'Owner'
      if (u && u.isOwner) return 'Admin'
      return 'Member'
    },
    memberRoleSelectValue: function (member) {
      var ea = member && member.emailAddress
      if (!ea) return 'member'
      return ea.roleNormalized === 'admin' ? 'admin' : 'member'
    },
    onMemberRoleChange: function (member, nextRole) {
      var self = this
      if (!this.isOwner || this.memberRoleIsLocked(member)) return
      var ea = member.emailAddress
      if (!ea) return
      var prev = this.memberRoleSelectValue(member)
      if (nextRole === prev) return
      var rawUserId = ea.userId != null ? ea.userId : ea.user_id
      if (rawUserId == null || rawUserId === '') {
        rawUserId = member.user && member.user.id != null ? member.user.id : null
      }
      var userId = rawUserId != null && typeof rawUserId === 'object' && rawUserId.value !== undefined ? rawUserId.value : rawUserId
      if (userId == null || userId === '') {
        this.flashError('Cannot update role: missing user id.')
        return
      }
      this.savingMemberRoleUserId = userId
      this.errorMsg = ''
      this.$http.patch('/api/v1/users/' + encodeURIComponent(String(userId)) + '/role', { role: nextRole }).then(function () {
        self.savingMemberRoleUserId = null
        self.flash('Role updated')
        self.fetchAll()
      }).catch(function (err) {
        self.savingMemberRoleUserId = null
        self.flashError((err.body && err.body.message) || err.message || 'Role update failed')
      })
    },
    openAssignSellersModal: function (member) {
      if (!member.emailAddress || !this.isOwner) return
      if (this.memberRoleIsLocked(member)) return
      this.assignModalMember = member
      this.$nextTick(function () {
        $('#assignSellersModal').modal('show')
      })
    },
    closeAssignSellersModal: function () {
      $('#assignSellersModal').modal('hide')
      this.assignModalMember = null
      this.savingAssignModal = false
    },
    confirmAssignSellersModal: function () {
      var self = this
      var member = this.assignModalMember
      if (!member || !member.emailAddress || !this.isOwner) {
        this.closeAssignSellersModal()
        return
      }
      var ea = member.emailAddress
      this.savingAssignModal = true
      this.errorMsg = ''
      this.persistNotificationPrefsSellerAssignments(ea, member.user).then(function () {
        self.savingAssignModal = false
        window.dirty = false
        self.flash('Assignments saved')
        $('#assignSellersModal').modal('hide')
        self.assignModalMember = null
        self.fetchAll()
      }).catch(function (err) {
        self.savingAssignModal = false
        var msg = (err.body && err.body.message) || err.message || 'Save failed'
        self.flashError(msg)
      })
    },
    assignModalSellerChecked: function (seller) {
      if (!this.assignModalMember || !this.assignModalMember.emailAddress) return false
      var id = String(this.sellerIdVal(seller))
      var list = this.assignModalMember.emailAddress.allowedSellerAccounts || []
      for (var i = 0; i < list.length; i++) {
        if (String(list[i]) === id) return true
      }
      return false
    },
    assignModalToggleSeller: function (seller) {
      if (!this.assignModalMember || !this.assignModalMember.emailAddress) return
      var ea = this.assignModalMember.emailAddress
      var id = this.sellerIdVal(seller)
      var list = (ea.allowedSellerAccounts || []).slice()
      var ix = -1
      for (var j = 0; j < list.length; j++) {
        if (String(list[j]) === String(id)) {
          ix = j
          break
        }
      }
      if (ix >= 0) list.splice(ix, 1)
      else list.push(id)
      this.$set(ea, 'allowedSellerAccounts', list)
    },
    prefsSellerIdsForEa: function (ea) {
      var self = this
      var list = this.sellers.filter(function (s) {
        var sid = self.sellerIdVal(s)
        return (ea.allowedSellerAccounts || []).some(function (id) { return String(id) === String(sid) })
      })
      return list.map(function (s) {
        var sid = self.sellerIdVal(s)
        var raw = sid != null && typeof sid === 'object' && sid.value !== undefined ? sid.value : sid
        if (raw == null || raw === '') return null
        var str = String(raw).trim()
        var n = Number(str)
        if (!isNaN(n) && isFinite(n) && /^-?\d+$/.test(str)) return n
        return str
      }).filter(function (x) { return x != null && x !== '' })
    },
    tenantUserIdForNotificationPrefs: function (email) {
      if (!email) return null
      var em = String(email).toLowerCase()
      for (var i = 0; i < this.tenantUsers.length; i++) {
        var u = this.tenantUsers[i]
        if (!u.email || String(u.email).toLowerCase() !== em || u.isOwner) continue
        var id = u.id
        var raw = id != null && typeof id === 'object' && id.value !== undefined ? id.value : id
        if (raw == null || raw === '') continue
        var str = String(raw).trim()
        var n = Number(str)
        if (!isNaN(n) && isFinite(n) && /^-?\d+$/.test(str)) return n
        return raw
      }
      return null
    },
    persistNotificationPrefsSellerAssignments: function (ea) {
      var userId = ea && ea.userId != null ? ea.userId : null
      var sellerIds = this.prefsSellerIdsForEa(ea)
      var payload = {
        seller_ids: sellerIds
      }
      if (userId != null && userId !== '') payload.user_id = userId
      return this.$http.put(NOTIFICATION_PREFS_SELLERS_URL_V1, payload)
    },
    changePassword: function () { this.$refs.changePasswordModal.show() },
    addStorefront: function () {
      if (!this.canAddStorefront) return
      this.initAddStorefrontModal()
      $('#addSellerAccountModal').modal('show')
    },
    initAddStorefrontModal: function () {
      destroySemanticModal('#addSellerAccountModal')
      var $m = $('#addSellerAccountModal')
      if (!$m.length) return
      $m.modal({
        detachable: true,
        autofocus: false,
        observeChanges: false,
        closable: true,
        onHidden: function () {
          clearStuckPageDimmer()
        }
      })
    },
    resetInviteMemberForm: function () {
      this.inviteEmail = ''
      this.inviteFirstName = ''
      this.inviteLastName = ''
    },
    inviteMember: function () {
      this.resetInviteMemberForm()
      $('#inviteMemberModal').modal('show')
    },
    closeInviteMemberModal: function () {
      $('#inviteMemberModal').modal('hide')
      this.resetInviteMemberForm()
    },
    submitInviteMember: function () {
      var email = this.inviteEmailTrimmed
      if (!email) {
        this.flashError('Enter an email address.')
        return
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.flashError('Enter a valid email address.')
        return
      }
      var self = this
      var timezoneOffsetMins = 0 - new Date().getTimezoneOffset()
      var payload = {
        email: email,
        firstName: (this.inviteFirstName || '').trim(),
        lastName: (this.inviteLastName || '').trim(),
        timezoneOffsetMins: timezoneOffsetMins
      }
      this.inviteSubmitting = true
      this.$http.post('/api/auth/addUser', payload).then(function () {
        self.inviteSubmitting = false
        self.flash('Invitation sent to ' + email + '. They will also receive an email to verify their address.')
        $('#inviteMemberModal').modal('hide')
        self.resetInviteMemberForm()
        self.fetchAll()
      }).catch(function (err) {
        self.inviteSubmitting = false
        var b = err && err.body
        var msg = (b && (b.error || b.message)) || err.message || 'Failed to send invitation'
        self.flashError(msg)
      })
    },
    saveProfile: function () {
      this.savingProfile = true
      var self = this
      this.$http.put('/api/identity/settings', {
        firstName: this.firstName,
        lastName: this.lastName,
        changedEmailAddresses: [],
        deletedEmailAddresses: [],
        sellerAccountNewNames: []
      }).then(function () {
        self.savingProfile = false
        self.firstNameOrig = self.firstName
        self.lastNameOrig = self.lastName
        window.dirty = false
        self.flash('Profile saved')
      }).catch(function (err) {
        self.savingProfile = false
        self.flashError((err.body && err.body.message) || 'Save failed')
      })
    },
    saveSellers: function () {
      var self = this
      var updates = []
      this.filteredSellers.forEach(function (s) {
        var id = self.sellerIdVal(s)
        if (id == null || id === '') return
        if (self.sellerNickNames[id] !== undefined && self.sellerNickNames[id] !== s.nickName) {
          updates.push({ id: id, name: String(self.sellerNickNames[id]).trim() })
        }
      })
      if (!updates.length) return
      this.savingSellers = true
      Promise.all(updates.map(function (u) {
        var path = '/api/v1/sellers/' + encodeURIComponent(String(u.id))
        return self.$http.put(path, { name: u.name })
      })).then(function () {
        self.savingSellers = false
        self.sellerNickNames = {}
        window.dirty = false
        self.flash('Storefronts saved')
        self.fetchAll()
      }).catch(function (err) {
        self.savingSellers = false
        self.flashError((err.body && err.body.message) || err.message || 'Save failed')
      })
    },
    saveTeam: function () {
      var self = this
      var dirtyRows = this.memberRows.filter(function (row) {
        var ea = row.emailAddress
        return !!(ea && !row.user.isOwner && !ea.isDeleted && !self.sellerAccessEqual(ea))
      })
      if (!dirtyRows.length) {
        this.flash('No assignment changes to save')
        return
      }

      this.savingTeam = true
      this.errorMsg = ''
      var chain = dirtyRows.map(function (row) {
        return self.persistNotificationPrefsSellerAssignments(row.emailAddress, row.user)
      })

      Promise.all(chain).then(function () {
        self.savingTeam = false
        window.dirty = false
        self.flash('Team assignments saved')
        self.fetchAll()
      }).catch(function (err) {
        self.savingTeam = false
        var msg = (err.body && err.body.message) || err.message || 'Save failed'
        self.flashError(msg)
      })
    },
    reauthorizeSeller: function (seller) {
      var data = {
        region: seller.region,
        marketplace: amazonAuthorizationQueryMarketplace(seller.region, seller.marketplaceId || seller.marketplace_id),
        path: this.$route.path,
        accName: seller.nickName,
        expectedSellerKey: this.sellerKeyVal(seller)
      }
      this.$http.get('/api/identity/amazon/authorization/redirect', { params: data }).then(function (response) {
        window.location = response.body.location
      }).catch(function (err) {
        window.alert((err.body && err.body.message) || 'Re-authorization failed')
      })
    },
    deleteSeller: function (seller) {
      if (!window.confirm('Delete storefront ' + (seller.nickName || this.sellerKeyVal(seller)) + '?')) return
      var sid = this.sellerIdVal(seller)
      if (sid == null || sid === '') {
        this.flashError('Cannot delete storefront: missing seller id.')
        return
      }
      var self = this
      var sidKey = String(sid)
      this.deletingSellerId = sidKey
      this.$http.delete('/api/v1/sellers/' + encodeURIComponent(sidKey)).then(function () {
        self.deletingSellerId = null
        self.flash('Storefront removed')
        self.fetchAll()
      }).catch(function (err) {
        self.deletingSellerId = null
        self.flashError((err.body && err.body.message) || 'Delete failed')
      })
    },
    removeMember: function (member) {
      if (!window.confirm('Remove ' + (member.user.fullName || member.user.email) + ' from the team?')) return
      if (this.memberRoleIsLocked(member)) {
        this.flashError('Cannot remove the workspace owner.')
        return
      }
      if (member.user && member.user.isOwner) {
        this.flashError('Cannot remove an admin account from here.')
        return
      }
      var ea = member.emailAddress
      var self = this
      if (!ea) {
        this.flashError('No email record found for this member.')
        return
      }
      var rawUserId = ea.userId != null ? ea.userId : ea.user_id
      if (rawUserId == null || rawUserId === '') {
        rawUserId = member.user && member.user.id != null ? member.user.id : null
      }
      var userId = rawUserId != null && typeof rawUserId === 'object' && rawUserId.value !== undefined ? rawUserId.value : rawUserId
      if (userId == null || userId === '') {
        this.flashError('Cannot remove member: missing user id.')
        return
      }
      this.$http.delete('/api/v1/users/' + encodeURIComponent(String(userId))).then(function () {
        self.flash('Member removed')
        self.fetchAll()
      }).catch(function (err) {
        self.flashError((err.body && err.body.message) || err.message || 'Remove failed')
      })
    },
    connectGoogleSheets: function () {
      this.$http.get('/api/googlesheets/authorizationurl').then(function (r) { window.open(r.body, '_blank') })
    },
    openGoogleSheet: function () {
      if (this.gsheetsConnection.spreadsheetId) window.open('https://docs.google.com/spreadsheets/d/' + this.gsheetsConnection.spreadsheetId, '_blank')
    },
    revokeGoogleSheets: function () {
      if (!window.confirm('Revoke Google Sheets connection?')) return
      var gs = this.authorizations.filter(function (a) { return a.name === 'Google Sheets' })
      if (gs.length) {
        var self = this
        this.$http.delete('/api/identity/authorization/' + gs[0].id).then(function () { self.fetchAll() })
      }
    },
    revokeZapier: function () {
      if (!window.confirm('Revoke Zapier connection?')) return
      var zapier = this.zapierAuthorizations
      if (!zapier.length) return
      var self = this
      this.$http.delete('/api/identity/authorization/' + zapier[0].id).then(function () {
        self.flash('Zapier connection revoked')
        self.fetchAll()
      }).catch(function (err) {
        self.flashError((err.body && err.body.message) || err.message || 'Revoke failed')
      })
    },
    onAddSellerError: function (msg) { this.flashError(msg) },
    flash: function (msg) { this.successMsg = msg; var self = this; setTimeout(function () { self.successMsg = '' }, 3000) },
    flashError: function (msg) { this.errorMsg = msg; var self = this; setTimeout(function () { self.errorMsg = '' }, 5000) }
  }
}
</script>

<style scoped>
.ats { position: relative; }
.ats-action-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(2px);
}
.ats-action-overlay-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 32px;
  border-radius: var(--radius-card, 8px);
  background: var(--bg-white, #fff);
  border: 1px solid var(--border-light, #e5e5e5);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
}
.ats-action-overlay-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary, #555);
}
.ats-loading { display: flex; align-items: center; justify-content: center; min-height: 300px; }
.ats-spinner { width: 36px; height: 36px; border: 3px solid var(--border-light); border-top-color: var(--primary-navy); border-radius: 50%; animation: ats-spin 0.7s linear infinite; }
@keyframes ats-spin { to { transform: rotate(360deg); } }
.ats-error { text-align: center; padding: 48px 24px; }
.ats-error-title { font-size: 18px; font-weight: 600; color: var(--text-primary); margin: 0 0 8px; }
.ats-error-msg { font-size: 14px; color: var(--text-secondary); margin: 0 0 16px; }

/* Header */
.ats-header { margin-bottom: 28px; }
.ats-title { font-size: 22px; font-weight: 700; color: var(--text-primary); margin: 0 0 6px; }
.ats-subtitle { font-size: 14px; color: var(--text-secondary); margin: 0; }

/* Section labels */
.ats-section-label { font-size: 11px; font-weight: 700; letter-spacing: 0.08em; color: var(--accent-orange); margin: 32px 0 12px; }

/* Overview grid */
.ats-overview-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 8px; }
.ats-ov-card { background: var(--bg-white); border: 1px solid var(--border-light); border-radius: var(--radius-card); padding: 20px; display: flex; flex-direction: column; gap: 4px; }
.ats-ov-label { font-size: 11px; font-weight: 600; letter-spacing: 0.05em; color: var(--text-muted); }
.ats-ov-value { font-size: 20px; font-weight: 700; color: var(--text-primary); }
.ats-ov-sub { font-size: 13px; color: var(--text-secondary); }

/* Profile */
.ats-profile-row { display: flex; gap: 20px; margin-bottom: 16px; }
.ats-profile-row .ats-field { flex: 1; max-width: 280px; }
.ats-field { margin-bottom: 16px; }
.ats-field-label { display: block; font-size: 11px; font-weight: 600; letter-spacing: 0.05em; color: var(--text-muted); margin-bottom: 6px; }
.ats-input { width: 100%; padding: 9px 12px; border: 1px solid var(--border-light); border-radius: var(--radius-btn); font-size: 14px; color: var(--text-primary); background: var(--bg-white); transition: border-color 0.15s; box-sizing: border-box; }
.ats-input:focus { outline: none; border-color: var(--primary-navy); }
.ats-input-readonly { background: var(--bg-light); color: var(--text-secondary); cursor: default; user-select: all; }
.ats-input-sm { padding: 6px 10px; font-size: 13px; }
.ats-profile-actions { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; margin-bottom: 8px; }

/* Card */
.ats-card { background: var(--bg-white); border: 1px solid var(--border-light); border-radius: var(--radius-card); padding: 24px; margin-bottom: 8px; }
.ats-card-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
.ats-card-title { font-size: 16px; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
.ats-card-desc { font-size: 13px; color: var(--text-secondary); margin: 0; max-width: 600px; }
.ats-card-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border-light); }

/* Tables */
.ats-table-wrap { overflow-x: auto; }
.ats-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ats-table th { text-align: left; font-size: 11px; font-weight: 600; letter-spacing: 0.05em; color: var(--text-muted); padding: 8px 12px; border-bottom: 1px solid var(--border-light); white-space: nowrap; }
.ats-table td { padding: 12px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
.ats-table tbody tr:last-child td { border-bottom: none; }
.ats-empty { text-align: center; color: var(--text-muted); padding: 32px 12px !important; }
.ats-cell-mono { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 12px; color: var(--text-secondary); }
.ats-cell-center { text-align: center; }
.ats-cell-actions { display: flex; align-items: center; gap: 8px; }

.ats-toggle {
  position: relative;
  display: inline-flex;
  width: 48px;
  height: 26px;
  cursor: pointer;
  flex-shrink: 0;
  vertical-align: middle;
}
.ats-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.ats-toggle-slider {
  border-radius: 999px;
  background: #c9d1dd;
  width: 48px;
  height: 26px;
  transition: background 0.15s ease;
  position: relative;
}
.ats-toggle-slider:after {
  content: '';
  position: absolute;
  left: 3px;
  top: 3px;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  transition: transform 0.18s ease;
}
.ats-toggle input:checked + .ats-toggle-slider:after { transform: translateX(22px); }
.ats-toggle input:checked + .ats-toggle-slider { background: var(--primary-navy); }
.ats-toggle input:disabled + .ats-toggle-slider { opacity: 0.55; }
.ats-toggle-busy { opacity: 0.6; pointer-events: none; }

/* Badges */
.ats-badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.ats-badge-ok { background: #ecfdf5; color: var(--status-success); }
.ats-badge-warn { background: #fef3c7; color: #92400e; }
.ats-badge-muted { background: var(--bg-light); color: var(--text-muted); }
.ats-badge-role-admin { background: var(--primary-navy); color: #fff; }
.ats-badge-role-member { background: var(--bg-light); color: var(--text-primary); border: 1px solid var(--border-light); }
.ats-role-select {
  min-width: 120px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-btn);
  background: var(--bg-white);
  color: var(--text-primary);
  cursor: pointer;
}
.ats-role-select:focus { outline: none; border-color: var(--primary-navy); }
.ats-role-select:disabled { opacity: 0.55; cursor: not-allowed; }

/* Marketplace badges */
.ats-mp-badges { display: flex; flex-wrap: wrap; gap: 8px 10px; align-items: center; }
.ats-mp-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; }

/* Buttons */
.ats-btn { display: inline-flex; align-items: center; justify-content: center; padding: 8px 18px; border-radius: var(--radius-btn); font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; border: none; white-space: nowrap; }
.ats-btn-primary { background: var(--primary-navy); color: #fff; }
.ats-btn-primary:hover:not(:disabled) { opacity: 0.9; }
.ats-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.ats-btn-outline { background: var(--bg-white); color: var(--text-primary); border: 1px solid var(--border-light); }
.ats-btn-outline:hover { background: var(--bg-light); }
.ats-btn-connect { background: #ef4444; color: #fff; font-size: 12px; padding: 6px 14px; border-radius: 20px; }
.ats-btn-connect:hover { opacity: 0.9; }
.ats-btn-loading { pointer-events: none; opacity: 0.7; }

/* Icon buttons */
.ats-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: var(--radius-btn); border: 1px solid var(--border-light); background: var(--bg-white); color: var(--text-secondary); cursor: pointer; transition: all 0.15s; }
.ats-icon-btn:hover:not(:disabled) { background: var(--bg-light); color: var(--text-primary); }
.ats-icon-btn:disabled { opacity: 0.45; cursor: not-allowed; pointer-events: none; }
.ats-icon-btn-danger:hover:not(:disabled) { background: #fef2f2; color: var(--status-critical); border-color: #fecaca; }

/* Members */
.ats-status-cell { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.ats-btn-verify-email { padding: 4px 12px; font-size: 12px; font-weight: 600; }
.ats-member-info { display: flex; flex-direction: column; gap: 2px; }
.ats-member-name { font-weight: 600; color: var(--text-primary); font-size: 14px; }
.ats-member-email { font-size: 12px; color: var(--primary-navy); text-decoration: underline; }

/* Account pills */
.ats-account-pills { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.ats-pill-account { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 6px; font-size: 12px; background: var(--bg-light); color: var(--text-primary); border: 1px solid var(--border-light); }
.ats-edit-link { font-size: 12px; color: var(--primary-navy); font-weight: 600; text-decoration: none; }
.ats-edit-link:hover { text-decoration: underline; }

/* Integrations */
.ats-integrations-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.ats-int-card { display: flex; align-items: center; gap: 16px; background: var(--bg-white); border: 1px solid var(--border-light); border-radius: var(--radius-card); padding: 20px; }
.ats-int-icon { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden; background: var(--bg-light); }
.ats-int-logo { width: 100%; height: 100%; object-fit: contain; display: block; }
.ats-int-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ats-int-name { font-size: 14px; font-weight: 700; color: var(--text-primary); }
.ats-int-detail { font-size: 12px; color: var(--text-secondary); }
.ats-int-actions { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }

/* Links */
.ats-link { color: var(--primary-navy); font-size: 13px; font-weight: 600; text-decoration: none; }
.ats-link:hover { text-decoration: underline; }
.ats-link-danger { color: var(--status-critical); }

/* Notes */
.ats-note { font-size: 13px; color: var(--text-secondary); }
.ats-member-readonly-hint { font-size: 13px; color: var(--text-secondary); margin: 0; text-align: right; }
.ats-cell-text { font-size: 14px; color: var(--text-primary); }
.ats-text-muted { color: var(--text-muted); }
.ats-text-sm { font-size: 12px; }

/* Toast */
.ats-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); padding: 12px 24px; border-radius: var(--radius-btn); font-size: 14px; font-weight: 600; z-index: 9999; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.ats-toast-ok { background: #ecfdf5; color: var(--status-success); border: 1px solid #bbf7d0; }
.ats-toast-err { background: #fef2f2; color: var(--status-critical); border: 1px solid #fecaca; }
.ats-slide-up-enter-active, .ats-slide-up-leave-active { transition: all 0.3s ease; }
.ats-slide-up-enter, .ats-slide-up-leave-to { opacity: 0; transform: translateX(-50%) translateY(20px); }

.assign-modal-intro { font-size: 14px; color: var(--text-secondary); margin: 0 0 16px; line-height: 1.45; }
.assign-modal-empty { font-size: 14px; color: var(--text-muted); }
.assign-seller-list { display: flex; flex-direction: column; gap: 10px; max-height: 360px; overflow-y: auto; }
.assign-seller-row { display: flex; align-items: center; gap: 12px; font-size: 14px; color: var(--text-primary); cursor: pointer; }
.assign-seller-row input[type="checkbox"] { flex-shrink: 0; cursor: pointer; }
.assign-seller-label { flex: 1; min-width: 0; font-weight: 600; }
.assign-seller-meta { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 12px; color: var(--text-muted); flex-shrink: 0; max-width: 120px; overflow: hidden; text-overflow: ellipsis; }

.invite-modal-intro { font-size: 14px; color: var(--text-secondary); margin: 0 0 16px; line-height: 1.45; }
.invite-modal-form { max-width: 440px; }
.invite-modal-name-row { display: flex; gap: 16px; }
.invite-modal-name-row .ats-field { flex: 1; min-width: 0; margin-bottom: 0; }

</style>



// WEBPACK FOOTER //
// AccountTeamSettings.vue?7153bff4